/**
 * PaywallModal \u2014 single modal that presents the three locked paywall
 * versions from Spec Section 5.
 *
 *   \u2022 Version A (one-time)    \u2014 "Get the Full Read \u2014 $4.99"
 *   \u2022 Version B (14-day pass) \u2014 "Start My 14-Day Pass \u2014 $14.99"
 *   \u2022 Version C (pass + monthly) \u2014 "Start My Monthly Membership \u2014 $24.99"
 *
 * Default lead pitch uses Version C (high-intent framing). Each tier card
 * carries its locked Version-specific body copy. Buttons are the locked
 * verbatim labels with em-dash + exact price.
 */
import { useEffect, useState } from "react";
import { X, Loader2 } from "lucide-react";
import { authedAxios } from "../lib/useAccess";
import { trackEvent } from "../lib/useAnalytics";
import { PAYWALL, PRIVACY } from "../lib/copy";

// Locked spec labels (em dash + exact price) keyed by tier.
const SPEC_BUTTON_LABEL = {
  one_full_read: PAYWALL.A.button,                  // "Get the Full Read \u2014 $4.99"
  fourteen_day_pass: PAYWALL.B.button,              // "Start My 14-Day Pass \u2014 $14.99"
  monthly_membership: PAYWALL.C.buttons.monthly,    // "Start My Monthly Membership \u2014 $24.99"
};

// Locked spec body copy per tier (verbatim).
const SPEC_TIER_BODY = {
  one_full_read: PAYWALL.A.body,
  fourteen_day_pass: PAYWALL.B.body,
  monthly_membership:
    "Ongoing monthly access to scan as many chats, profiles, and screenshots as you need.",
};

export const PaywallModal = ({ open, onClose, products }) => {
  const [buying, setBuying] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setError("");
      setBuying(null);
    }
  }, [open]);

  if (!open) return null;

  const ordered = ["one_full_read", "fourteen_day_pass", "monthly_membership"]
    .map((t) => products.find((p) => p.tier === t))
    .filter(Boolean);

  const buy = async (tier) => {
    setError("");
    setBuying(tier);
    trackEvent("checkout_clicked", { tier });
    try {
      const { data } = await authedAxios().post("/checkout/create-session", {
        tier,
        origin_url: window.location.origin,
      });
      if (data && data.url) {
        window.location.assign(data.url);
        return;
      }
      setError("We couldn\u2019t start checkout. Please try again.");
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        "We couldn\u2019t reach Stripe right now. Please try again in a moment.";
      setError(typeof msg === "string" ? msg : "Checkout failed. Try again.");
    } finally {
      setBuying(null);
    }
  };

  return (
    <div
      data-testid="paywall-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="paywall-title"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-3 sm:p-6"
      style={{ background: "rgba(42, 36, 32, 0.55)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="ss-panel relative w-full max-w-3xl max-h-[92vh] overflow-y-auto"
        style={{ borderColor: "var(--ss-rule-line)" }}
      >
        <button
          onClick={onClose}
          data-testid="paywall-close-btn"
          aria-label="Close"
          className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-black/5"
          style={{ color: "var(--ss-muted)" }}
        >
          <X className="w-5 h-5" strokeWidth={2.25} />
        </button>

        <div className="p-6 sm:p-8">
          {/* Lead pitch \u2014 Version C verbatim */}
          <p
            className="ss-eyebrow mb-2"
            style={{ color: "var(--ss-gold-deep)" }}
            data-testid="paywall-eyebrow"
          >
            The Full Read
          </p>
          <h2
            id="paywall-title"
            className="ss-display-tight text-2xl sm:text-3xl leading-tight mb-3"
            style={{ color: "var(--ss-ink)" }}
            data-testid="paywall-title"
          >
            Pick the plan that fits your week.
          </h2>
          <p
            className="text-base sm:text-lg leading-relaxed mb-7"
            style={{ color: "var(--ss-ink-2)" }}
            data-testid="paywall-version-c-body"
          >
            {PAYWALL.C.body}
          </p>

          {error && (
            <div
              data-testid="paywall-error"
              className="mb-5 p-3 rounded-lg text-sm"
              style={{
                background: "var(--ss-red-bg)",
                borderLeft: "3px solid var(--ss-red)",
                color: "var(--ss-ink)",
              }}
            >
              {error}
            </div>
          )}

          {/* Three tier cards \u2014 each carries its locked body + locked button label */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {ordered.map((p) => {
              const isBuying = buying === p.tier;
              const featured = p.tier === "fourteen_day_pass";
              return (
                <div
                  key={p.tier}
                  data-testid={`paywall-tier-${p.tier}`}
                  className="rounded-2xl p-5 flex flex-col relative"
                  style={{
                    background: featured ? "var(--ss-green-bg)" : "var(--ss-card-2)",
                    border: `1.5px solid ${
                      featured ? "var(--ss-green-rail)" : "var(--ss-rule-line)"
                    }`,
                  }}
                >
                  {featured && (
                    <span
                      className="absolute -top-3 left-5 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider font-semibold"
                      style={{ background: "var(--ss-green)", color: "white" }}
                    >
                      Dating Sprint Package
                    </span>
                  )}
                  <p
                    className="ss-eyebrow mb-1"
                    style={{
                      color: featured ? "var(--ss-green)" : "var(--ss-gold-deep)",
                    }}
                    data-testid={`paywall-tier-${p.tier}-name`}
                  >
                    {p.name.replace("DateWise Wingman ", "")}
                  </p>
                  <p
                    className="ss-display-tight text-3xl mb-3 leading-none"
                    style={{ color: "var(--ss-ink)" }}
                    data-testid={`paywall-tier-${p.tier}-price`}
                  >
                    {p.display_price}
                  </p>
                  <p
                    className="text-sm mb-5 leading-relaxed flex-1"
                    style={{ color: "var(--ss-ink-2)" }}
                    data-testid={`paywall-tier-${p.tier}-body`}
                  >
                    {SPEC_TIER_BODY[p.tier]}
                  </p>
                  <button
                    data-testid={`paywall-buy-${p.tier}`}
                    onClick={() => buy(p.tier)}
                    disabled={isBuying}
                    className={
                      featured
                        ? "ss-cta ss-cta-mode-primary mt-auto justify-center"
                        : "ss-cta mt-auto justify-center"
                    }
                  >
                    {isBuying ? (
                      <>
                        <Loader2
                          className="w-4 h-4 animate-spin"
                          strokeWidth={2.25}
                        />
                        Redirecting\u2026
                      </>
                    ) : (
                      SPEC_BUTTON_LABEL[p.tier]
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Privacy Version B (checkout reassurance under paywall buttons) */}
          <p
            className="mt-7 text-xs leading-relaxed"
            style={{ color: "var(--ss-muted)" }}
            data-testid="paywall-privacy-note"
          >
            {PRIVACY.B}
          </p>
        </div>
      </div>
    </div>
  );
};
